import * as React from 'react';
import { useState, useEffect, ReactNode } from 'react';
import * as yup from 'yup';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { Facebook, Twitter } from '@shadcn/custom/BrandIcons';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import { AuthPageShell } from 'app/pages/auth-pages/AuthPageShell';
import { AppLink } from 'app/reusable/Link';
import { Button } from '@shadcn/ui';
import { FormikInputWrapper } from '@shadcn/custom/FormikInputWrapper';
import { GoogleLogin } from '@react-oauth/google';

import authServices from 'app/services/auth-services';
import { trackGoogleAnalyticsEvent } from 'app/utils/google-analytics';
import { SignUpCategory, SignUpCompletedAction } from 'app/utils/google-analytics/events/signUp';
import { LoginCategory, LoginCompletedAction } from 'app/utils/google-analytics/events/login';

const LOGIN_TYPE = 'login';
const SIGNUP_TYPE = 'signup';
const HOME_URL = '/dashboard/home';

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const typeToConfig = {
  login: {
    text: 'LOG IN',
    authMethod: authServices.signIn,
  },
  signup: {
    text: 'SIGN UP',
    authMethod: authServices.signUp,
  },
  default: {
    text: 'LOG IN',
    authMethod: authServices.signIn,
  },
};

export const Login = ({ disableSmLogin, type }) => {
  const { tempToken } = useParams();
  const [searchParams] = useSearchParams();
  const tempSignUpToken = searchParams.get('tempSignUpToken');
  const { authToken, setAuthToken } = useJumboAuth();
  const navigate = useNavigate();
  const [serverMessage, setServerMessage] = useState('');

  const errorHandler = (error) => {
    if (error?.response) {
      const errors = error.response?.data?.errors;
      const errorObject = errors[0];
      const message = errorObject?.detail || 'Looks like there was an error. Please try again!';
      setServerMessage(message);
    }
  };

  const loginWithToken = async (tempToken) => {
    try {
      const currentUser = await authServices.getUserByTempToken(tempToken);
      if (currentUser?.attributes?.token) {
        setAuthToken(currentUser.attributes.token);
        navigate(HOME_URL);
        trackGoogleAnalyticsEvent(LoginCategory, LoginCompletedAction, window.location.pathname);
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    if (authToken) {
      navigate(HOME_URL);
    }

    if (tempToken) {
      loginWithToken(tempToken);
    }
  }, [tempToken, authToken]);

  const config = typeToConfig[type || LOGIN_TYPE];

  const onGoogleSignIn = async (credentialResponse) => {
    setServerMessage('');
    try {
      const response = await authServices.signInWithGoogle(credentialResponse);
      if (response?.attributes?.token) {
        setAuthToken(response?.attributes?.token);
        navigate(HOME_URL);
        if (!type) {
          trackGoogleAnalyticsEvent(LoginCategory, LoginCompletedAction, window.location.pathname);
        }
        if (type === SIGNUP_TYPE) {
          trackGoogleAnalyticsEvent(SignUpCategory, SignUpCompletedAction, window.location.pathname);
        }
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const onSubmit = async (email, password) => {
    setServerMessage('');

    try {
      const authMethod = config.authMethod;
      const authPayload = type === SIGNUP_TYPE ? { email, password, token: tempSignUpToken } : { email, password };
      const response = await authMethod(authPayload);
      if (response?.attributes?.token) {
        setAuthToken(response?.attributes?.token);
        navigate(HOME_URL);
        if (!type) {
          trackGoogleAnalyticsEvent(LoginCategory, LoginCompletedAction, window.location.pathname);
        }
        if (type === SIGNUP_TYPE) {
          trackGoogleAnalyticsEvent(SignUpCategory, SignUpCompletedAction, window.location.pathname);
        }
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const renderForm = (): ReactNode => (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full justify-center">
        <GoogleLogin
          onSuccess={(credentialResponse) => onGoogleSignIn(credentialResponse)}
          size="large"
          text="continue_with"
          shape="rectangular"
          type="standard"
          logo_alignment="left"
          width="100%"
        />
      </div>
      <div className="my-4 text-center text-gray-500">OR</div>
      <Formik
        validateOnChange={true}
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(data, { setSubmitting }) => {
          setSubmitting(true);
          onSubmit(data.email, data.password);
          setSubmitting(false);
        }}
      >
        {({ isSubmitting }) => (
          <Form className="flex w-full flex-col gap-4 text-left" noValidate autoComplete="off">
            <FormikInputWrapper label="Email" name="email" placeholder="you@company.com" />
            <FormikInputWrapper label="Password" name="password" placeholder="Password" type="password" />
            {serverMessage ? (
              <p className="rounded bg-destructive/25 p-3 text-destructive-foreground">{serverMessage}</p>
            ) : null}
            <Button
              className="w-full bg-primary hover:bg-primary/90 [&>span]:text-white"
              type="submit"
              variant="default"
              size="lg"
              loading={isSubmitting}
            >
              <span className="text-white">{config.text}</span>
            </Button>
            {disableSmLogin ? null : (
              <>
                <p className="mb-2">Or sign in with</p>
                <div className="mb-1 flex flex-row items-center gap-1">
                  <Button size="icon" aria-label="Facebook" className="bg-[#385196] hover:bg-[#385196]">
                    <Facebook />
                  </Button>
                  <Button size="icon" aria-label="Twitter" className="bg-[#00a8ff] hover:bg-[#00a8ff]">
                    <Twitter />
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
      {type === SIGNUP_TYPE ? (
        <h5 className="mt-4 text-center text-gray-700">
          Already have an account?{' '}
          <AppLink to="/user/login" className="font-medium text-primary hover:text-primary/90">
            Log in
          </AppLink>
        </h5>
      ) : null}
    </div>
  );

  return <AuthPageShell rightContent={renderForm()} />;
};
